'use client'

import { useLayoutEffect, useRef, useState, type ReactNode } from 'react'

interface Props {
  children: ReactNode
  gapClassName?: string
  className?: string
}

export default function DraggableCarousel({
  children,
  gapClassName = 'gap-5 md:gap-6',
  className = '',
}: Props) {
  const viewportRef = useRef<HTMLDivElement>(null)
  const trackRef = useRef<HTMLDivElement>(null)

  const [offset, setOffset] = useState(0)
  const [maxOffset, setMaxOffset] = useState(0)
  const [step, setStep] = useState(320)

  useLayoutEffect(() => {
    const compute = () => {
      const viewport = viewportRef.current
      const track = trackRef.current
      if (!viewport || !track) return

      const max = Math.max(0, track.scrollWidth - viewport.clientWidth)
      setMaxOffset(max)

      const firstChild = track.firstElementChild as HTMLElement | null
      if (firstChild) {
        const styles = getComputedStyle(track)
        const gap = parseFloat(styles.columnGap || styles.gap || '0') || 0
        setStep(firstChild.offsetWidth + gap)
      }

      setOffset((o) => Math.min(o, max))
    }

    compute()
    window.addEventListener('resize', compute)
    return () => window.removeEventListener('resize', compute)
  }, [children])

  const goPrev = () => setOffset((o) => Math.max(0, o - step))
  const goNext = () => setOffset((o) => Math.min(maxOffset, o + step))

  const atStart = offset <= 0
  const atEnd = offset >= maxOffset - 1
  const showArrows = maxOffset > 0

  const btnBase =
    'after:hidden w-11 h-11 rounded-full flex items-center justify-center transition-all duration-200'
  const btnActive =
    'bg-brand-500 border border-brand-500 text-white hover:bg-brand-600 hover:border-brand-600'
  const btnDisabled =
    'border border-charcoal-200 text-charcoal-400 opacity-50 cursor-not-allowed'

  const prevSvg = (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden>
      <path
        d="M9 11L4 7L9 3"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
  const nextSvg = (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden>
      <path
        d="M5 3L10 7L5 11"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )

  return (
    <div className="relative">
      <div
        ref={viewportRef}
        className={['relative overflow-hidden', className].join(' ')}
      >
        <div
          ref={trackRef}
          className={`flex ${gapClassName} w-max will-change-transform`}
          style={{
            transform: `translate3d(${-offset}px, 0, 0)`,
            transition: 'transform 700ms cubic-bezier(0.22, 1, 0.36, 1)',
          }}
        >
          {children}
        </div>

        {/* ─── Edge fades (md+ only) ────────────────────────────────
            Mask gradients sit inside the viewport on top of the
            track. They fade to the section background (#fdfcfb) so
            the off-screen cards visually dissolve at the carousel
            edges. Each mask is shown only when content actually
            extends past that edge — left fade hides at the start,
            right fade hides at the end. Mobile view intentionally
            untouched per design request.
            ────────────────────────────────────────────────────── */}
        <div
          aria-hidden
          className={`hidden md:block pointer-events-none absolute inset-y-0 left-0 w-16 lg:w-24 bg-gradient-to-r from-[#fdfcfb] to-transparent transition-opacity duration-300 ${
            atStart ? 'opacity-0' : 'opacity-100'
          }`}
        />
        <div
          aria-hidden
          className={`hidden md:block pointer-events-none absolute inset-y-0 right-0 w-16 lg:w-24 bg-gradient-to-l from-[#fdfcfb] to-transparent transition-opacity duration-300 ${
            atEnd ? 'opacity-0' : 'opacity-100'
          }`}
        />
      </div>

      {/* Arrows centred horizontally below the carousel. */}
      {showArrows && (
        <div className="flex justify-center gap-3 mt-8 md:mt-10">
          <button
            type="button"
            onClick={goPrev}
            disabled={atStart}
            aria-label="Previous"
            className={`${btnBase} ${atStart ? btnDisabled : btnActive}`}
          >
            {prevSvg}
          </button>
          <button
            type="button"
            onClick={goNext}
            disabled={atEnd}
            aria-label="Next"
            className={`${btnBase} ${atEnd ? btnDisabled : btnActive}`}
          >
            {nextSvg}
          </button>
        </div>
      )}
    </div>
  )
}
